import type { AxiosResponse } from 'axios'

import apiClient from './client'
import API from '@/api/API'
import { uploadDashboardFile } from '@/lib/api/materials'
import {
  SourceListResponse,
  SourceDetailResponse,
  SourceResponse,
  SourceStatusBatchItem,
  SourceStatusResponse,
  CreateSourceRequest,
  UpdateSourceRequest
} from '@/lib/types/api'

export const sourcesApi = {
  list: async (params?: {
    notebook_id?: string
    limit?: number
    offset?: number
    sort_by?: 'created' | 'updated'
    sort_order?: 'asc' | 'desc'
  }) => {
    const response = await apiClient.get<SourceListResponse[]>(API.sources.base, { params })
    return response.data
  },

  get: async (id: string) => {
    const response = await apiClient.get<SourceDetailResponse>(`${API.sources.base}/${id}`)
    return response.data
  },

  create: async (data: CreateSourceRequest & { file?: File }) => {
    let sourceData = data
    let uploadedSourceFile = false
    const dataWithFile = data as CreateSourceRequest & { file?: File }
    if (data.type === 'upload' && dataWithFile.file instanceof File) {
      const uploaded = await uploadDashboardFile(dataWithFile.file, 'file')
      if (!uploaded.url) {
        throw new Error('文件已上传，但 fs 未返回可访问的原文件 URL')
      }
      uploadedSourceFile = true
      sourceData = {
        ...data,
        title: data.title || dataWithFile.file.name,
        file_path: uploaded.fileUrl,
        file_url: uploaded.url,
      }
    }

    // Always use FormData to match backend expectations
    const formData = new FormData()

    // Add basic fields
    formData.append('type', sourceData.type)

    if (sourceData.notebooks !== undefined) {
      formData.append('notebooks', JSON.stringify(sourceData.notebooks))
    }
    if (sourceData.notebook_id) {
      formData.append('notebook_id', sourceData.notebook_id)
    }
    if (sourceData.title) {
      formData.append('title', sourceData.title)
    }
    if (sourceData.url) {
      formData.append('url', sourceData.url)
    }
    if (sourceData.file_path) {
      formData.append('file_path', sourceData.file_path)
    }
    if (sourceData.file_url) {
      formData.append('file_url', sourceData.file_url)
    }
    if (sourceData.content) {
      formData.append('content', sourceData.content)
    }
    if (sourceData.skills !== undefined) {
      formData.append('skills', JSON.stringify(sourceData.skills))
    }
    if (sourceData.language) {
      formData.append('language', sourceData.language)
    }

    if (!uploadedSourceFile && dataWithFile.file instanceof File) {
      formData.append('file', dataWithFile.file)
    }

    formData.append('embed', String(sourceData.embed ?? false))
    formData.append('build_graph', String(sourceData.build_graph ?? false))
    formData.append('delete_source', String(sourceData.delete_source ?? false))
    formData.append('async_processing', String(sourceData.async_processing ?? false))
    formData.append('enable_ocr', String(sourceData.enable_ocr ?? false))

    const response = await apiClient.post<SourceResponse>(API.sources.base, formData)
    return response.data
  },

  update: async (id: string, data: UpdateSourceRequest) => {
    const response = await apiClient.put<SourceListResponse>(`${API.sources.base}/${id}`, data)
    return response.data
  },

  delete: async (id: string) => {
    await apiClient.delete(`${API.sources.base}/${id}`)
  },

  status: async (id: string) => {
    const response = await apiClient.get<SourceStatusResponse>(`${API.sources.base}/${id}/status`)
    return response.data
  },

  statusBatch: async (notebookId: string) => {
    if (!notebookId) {
      return [] as SourceStatusBatchItem[]
    }

    const response = await apiClient.get<SourceStatusBatchItem[]>(API.sources.statusBatch, {
      params: { notebook_id: notebookId },
    })
    return response.data
  },

  upload: async (file: File, notebook_id: string) => {
    return sourcesApi.create({
      type: 'upload',
      notebook_id,
      async_processing: true,
      file,
    })
  },

  retry: async (id: string) => {
    const response = await apiClient.post<SourceResponse>(`${API.sources.base}/${id}/retry`)
    return response.data
  },

  downloadFile: async (id: string): Promise<AxiosResponse<Blob>> => {
    return apiClient.get(`${API.sources.base}/${id}/download`, {
      responseType: 'blob',
    })
  },
}
